// components/TreatmentMedications.tsx
import React, { CSSProperties, useState, useEffect } from 'react';
import { confirmButton, cancelButton } from '../../../components/Buttons';
import './TreatmentMedications.scss';
import { GenericPopup } from './popups/GenericPopup';
import {
  MEDICATION_GROUPS,
  DEFAULT_SELECTED_IDS,
  DEFAULT_ENABLED_GROUPS,
  MedicationGroup,
  MedicationOption
} from '../utils/asthmaMedicationsData';

import protocolPdf from '../utils/asthma_protocol.pdf';

interface TreatmentMedicationsProps {
  /**
   * Returns the selected drug names (labels) to the parent.
   */
  onSelectionChange?: (medicationLabels: string[]) => void;
  // check if steroids given already
  hasGivenSteroids: boolean;
}

// Calculating a list of all options 
const ALL_OPTIONS: MedicationOption[] = MEDICATION_GROUPS.flatMap(g => g.options);

const TreatmentMedications: React.FC<TreatmentMedicationsProps> = ({
  onSelectionChange,
  hasGivenSteroids,
}) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(DEFAULT_SELECTED_IDS);

  // edit state
  const [isEditing, setIsEditing] = useState(false);
  const [draftIds, setDraftIds] = useState<string[]>(DEFAULT_SELECTED_IDS);
  const [enabledGroups, setEnabledGroups] = useState<Record<string, boolean>>(DEFAULT_ENABLED_GROUPS);

  // State for steroids pop up
  const [showSteroidWarning, setShowSteroidWarning] = useState(false);

  const startEdit = () => {
    setDraftIds(selectedIds);
    setIsEditing(true);
  };

  const cancelEdit = () => {
    setDraftIds(selectedIds);
    setIsEditing(false);
    setShowSteroidWarning(false);
  };

  const performSave = () => {
    const filtered = draftIds.filter(id =>
      MEDICATION_GROUPS.some(
        g => enabledGroups[g.id] && g.options.some(o => o.id === id)
      )
    );
    setSelectedIds(filtered);
    setIsEditing(false);
    setShowSteroidWarning(false);
  };

  const handleSaveClick = () => {
    // 1. check if selected steroids
    const steroidsGroup = MEDICATION_GROUPS.find(g => g.id === 'steroids');
    const isSelectingSteroids = steroidsGroup?.options.some(opt => draftIds.includes(opt.id));

    // 2. check if the steroids group is in
    const isSteroidsEnabled = enabledGroups['steroids'];

    // 3. if given steroids show the pop up
    if (hasGivenSteroids && isSelectingSteroids && isSteroidsEnabled) {
      setShowSteroidWarning(true);
    } else {
      // else -> save normally
      performSave();
    }
  };

  // === the remove button in the pop up===
  const handleRemoveSteroids = () => {
    const steroidIds = MEDICATION_GROUPS.find(g => g.id === 'steroids')?.options.map(o => o.id) || [];

    // remove from draft
    setDraftIds(prev => prev.filter(id => !steroidIds.includes(id)));
    setShowSteroidWarning(false);
  };

  const toggleOption = (group: MedicationGroup, optionId: string) => {
    if (!enabledGroups[group.id]) {
      setEnabledGroups(prev => ({ ...prev, [group.id]: true }));
    }

    setDraftIds((prev) => {
      if (group.singleSelect) {
        const idsWithoutGroup = prev.filter(
          id => !group.options.some(o => o.id === id)
        );

        if (prev.includes(optionId)) {
          return idsWithoutGroup;
        } else {
          return [...idsWithoutGroup, optionId];
        }
      }

      if (prev.includes(optionId)) {
        return prev.filter(id => id !== optionId);
      }
      return [...prev, optionId];
    });
  };

  const toggleGroupEnabled = (group: MedicationGroup) => {
    setEnabledGroups(prev => {
      const nextEnabled = !prev[group.id];

      setDraftIds((currentDraft) => {
        if (!nextEnabled) {
          return currentDraft.filter(
            id => !group.options.some(o => o.id === id)
          );
        }
        return currentDraft;
      });

      return { ...prev, [group.id]: nextEnabled };
    });
  };

  const selectedOptions = ALL_OPTIONS.filter(o => selectedIds.includes(o.id));

  useEffect(() => {
    if (!onSelectionChange) return;
    const labels = ALL_OPTIONS.filter(o => selectedIds.includes(o.id)).map(o => o.label);
    onSelectionChange(labels);
  }, [onSelectionChange, selectedIds]);

  return (
    <div className="asthma-wrapper">
    <div className="treatment-medications-root">
      {/* summary display (when edit is closed) */}
      {!isEditing && (
        <div className="treatment-medications-summary-card">
          {/* edit button - located absolutely in SCSS */}
          <button
            type="button"
            className="treatment-medications-edit-button"
            onClick={startEdit}
            aria-label="עריכת טיפול תרופתי"
          >
            {/* SVG Pencil Icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              style={{ width: '16px', height: '16px' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
            </svg>
          </button>

          <ul className="treatment-medications-summary-list">
            {selectedOptions.map((opt) => (
              <li key={opt.id}>{opt.label}</li>
            ))}
          </ul>
        </div>
      )}

      {/* edit view */}
      {isEditing && (
        <div className="treatment-medications-window">
          <div className="treatment-medications-window-header">
            איזה טיפול תרופתי ברצונך לתת כרגע?
          </div>

          {/* === the header with the link to the protocol === */}
          <div className="treatment-medications-window-subtitle">

            {/* 1. alert icon (Badge) */}
            <div className="protocol-alert-badge">
              !
            </div>

            {/* 2. the whole text is now a link */}
            <a
              href={protocolPdf}
              target="_blank"
              rel="noopener noreferrer"
              className="protocol-content-link"
            >
              הטיפול המוצע נבחר מתוך פרוטוקול אסתמה. רוצה להציץ בפרוטוקול?
            </a>

            {/* 3. document icon */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="protocol-doc-icon"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
          </div>
          {MEDICATION_GROUPS.map((group) => {
            const groupEnabled = enabledGroups[group.id];

            // Special UI for fluids, adrenaline, magnesium - no grouping header, just simple toggles
            const isNoHeaderGroup = ['fluids', 'adrenaline', 'magnesium'].includes(group.id);

            return (
              <div key={group.id} className="treatment-medications-group">
                {!isNoHeaderGroup && (
                  <div className="treatment-medications-group-header">
                    <span className="treatment-medications-group-title">
                      {group.title}
                    </span>

                    {/* V box on the left, next to the title */}
                    {group.id !== 'route' ? (
                       <label className="treatment-medications-group-toggle-label">
                         <input
                           type="checkbox"
                           className="treatment-medications-group-toggle-input"
                           checked={groupEnabled}
                           onChange={() => toggleGroupEnabled(group)}
                         />
                         <span className="treatment-medications-group-toggle-checkmark"></span>
                       </label>
                    ) : null}
                  </div>
                )}

                <ul className="treatment-medications-options-list">
                  {group.options.map((opt) => {
                    // For the groups without header - they act as standalone checkboxes
                    if (isNoHeaderGroup) {
                      const checked = draftIds.includes(opt.id);
                      return (
                        <li
                          key={opt.id}
                          className={
                            'treatment-medications-option-row' +
                            (checked ? ' treatment-medications-option-checked' : '')
                          }
                        >
                          <label className="treatment-medications-option-label">
                            <input
                              type={group.singleSelect ? 'radio' : 'checkbox'}
                              name={group.id}
                              checked={checked}
                              onChange={() => {
                                // For standalone, toggle group enabled automatically
                                if (!enabledGroups[group.id]) {
                                  setEnabledGroups(prev => ({ ...prev, [group.id]: true }));
                                } else if (checked) {
                                  // if turning off the only option, disable group too
                                  setEnabledGroups(prev => ({ ...prev, [group.id]: false }));
                                }
                                toggleOption(group, opt.id);
                              }}
                            />
                            <span className="treatment-medications-option-text">
                              {opt.label}
                            </span>
                          </label>
                        </li>
                      );
                    }

                    // Normal flow for nested options
                    const isRouteGroup = group.id === 'route';
                    // Route group components should only be enabled if steroids are enabled
                    const isEnabled = isRouteGroup ? enabledGroups['steroids'] : groupEnabled;
                    const checked = draftIds.includes(opt.id) && isEnabled;

                    return (
                      <li
                        key={opt.id}
                        className={
                          'treatment-medications-option-row' +
                          (checked ? ' treatment-medications-option-checked' : '')
                        }
                      >
                        <label className="treatment-medications-option-label">
                          <input
                            type={group.singleSelect ? 'radio' : 'checkbox'}
                            name={group.id}
                            checked={checked}
                            disabled={!isEnabled}
                            onChange={() => toggleOption(group, opt.id)}
                          />
                          <span className="treatment-medications-option-text">
                            {opt.label}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}

          <div className="treatment-medications-actions">
            <button
              type="button"
              style={cancelButton as CSSProperties}
              onClick={cancelEdit}
            >
              ביטול
            </button>
            <button
              type="button"
              style={confirmButton as CSSProperties}
              onClick={handleSaveClick}
            >
              שמירת שינויים
            </button>
          </div>
        </div>
      )}

      {/* === steroids warning pop up ===*/}
      <GenericPopup
        isOpen={showSteroidWarning}
        title="כבר ניתנו סטרואידים"
        onClose={() => setShowSteroidWarning(false)}

        primaryButtonText="הוסף סטרואידים"
        onPrimaryClick={performSave}

        secondaryButtonText="הסר"
        onSecondaryClick={handleRemoveSteroids}
      >
        <p>
          יש לשים לב כי כבר ניתנו סטרואידים במהלך הטיפול.
          <br />
          כלל, ההמלצה היא לתת סטרואידים אחת ל- 24 שעות,
          אלא בשיקול דעת רפואי חריג.
        </p>
      </GenericPopup>

    </div>
    </div>
  );
};

export default TreatmentMedications;